import { type FormEvent, type ReactNode, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/cn';
import { AuthShell } from './AuthShell';

export interface Field {
  id: string;
  label: string;
  type: 'text' | 'email' | 'password';
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string | undefined;
  placeholder?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
}

interface AuthFormProps {
  title: string;
  subtitle: string;
  fields: Field[];
  submitLabel: string;
  submitting: boolean;
  onSubmit: () => void | Promise<void>;
  footer: ReactNode;
}

/** Centered, theme-aware auth card used by Login, Signup, Forgot, Reset. */
export function AuthForm({
  title,
  subtitle,
  fields,
  submitLabel,
  submitting,
  onSubmit,
  footer,
}: AuthFormProps) {
  // Which password fields are currently revealed (eye toggle).
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  function handle(e: FormEvent) {
    e.preventDefault();
    void onSubmit();
  }

  return (
    <AuthShell title={title} subtitle={subtitle}>
      <form onSubmit={handle} noValidate className="space-y-4">
        {fields.map((f) => {
          const isPassword = f.type === 'password';
          const show = revealed[f.id] ?? false;
          return (
            <div key={f.id} className="space-y-1.5">
              <label htmlFor={f.id} className="block text-xs font-medium text-foreground">
                {f.label}
                {f.required && (
                  <span className="ml-0.5 text-destructive" aria-hidden>
                    *
                  </span>
                )}
              </label>
              <div className="relative">
                <input
                  id={f.id}
                  type={isPassword && show ? 'text' : f.type}
                  value={f.value}
                  onChange={(e) => f.onChange(e.target.value)}
                  autoComplete={f.autoComplete}
                  placeholder={f.placeholder}
                  aria-invalid={f.error ? true : undefined}
                  className={cn(
                    'h-10 w-full rounded-lg border bg-background px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:ring-2',
                    isPassword && 'pr-10',
                    f.error
                      ? 'border-destructive focus:border-destructive focus:ring-destructive/20'
                      : 'border-border focus:border-foreground focus:ring-foreground/10',
                  )}
                />
                {isPassword && (
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setRevealed((r) => ({ ...r, [f.id]: !show }))}
                    aria-label={show ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                )}
              </div>
              {f.error && <p className="text-xs text-destructive">{f.error}</p>}
            </div>
          );
        })}
        <button
          type="submit"
          disabled={submitting}
          className="mt-1 inline-flex h-10 w-full items-center justify-center rounded-lg bg-foreground px-3 text-sm font-medium text-background shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Please wait…' : submitLabel}
        </button>
      </form>

      <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>
    </AuthShell>
  );
}
